import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Container, 
  Paper, 
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Group as GroupIcon, Send as SendIcon } from '@mui/icons-material';
import AuthDialog from '../components/AuthDialog';
import { getDb, waitForFirebase } from '../services/firebase';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, arrayUnion } from 'firebase/firestore';
import VideoTable from '../pages/ManagePages/VideoTable';
import { COLUMNS, PAGE_COLUMN_KEYS } from '../pages/ManagePages/tableConfig';
import VideoTableRow from '../pages/ManagePages/VideoTableRow';

const GroupPage: React.FC = () => {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [group, setGroup] = useState<any>(null);
  const [videos, setVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showJoinRequest, setShowJoinRequest] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  // 判斷是否為公開頁面
  const isPublicPage = true; // group 頁面本來就是公開頁

  useEffect(() => {
    if (!user && !isPublicPage) {
      setShowLogin(true);
    } else {
      setShowLogin(false);
    }
  }, [user, isPublicPage]);

  useEffect(() => {
    const fetchGroupAndVideos = async () => {
      if (!groupId) return;
      setLoading(true);
      setError(null);
      try {
        // ✅ 等待 Firebase 初始化
        await waitForFirebase();
        const db = getDb();
        if (!db) {
          setError('Firestore 尚未初始化，請稍後再試');
          setLoading(false);
          return;
        }
        
        const groupDoc = await getDoc(doc(db, 'groups', groupId));
        if (!groupDoc.exists()) {
          setError('找不到此群組');
          setLoading(false);
          return;
        }
        const groupData = groupDoc.data();
        setGroup(groupData);
        const q = query(collection(db, 'sharedVbids'), where('groupIds', 'array-contains', groupId));
        const snap = await getDocs(q);
        const groupVideos = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setVideos(groupVideos);
      } catch (e: any) {
        console.error('載入群組失敗:', e);
        setError(e.message || '載入群組失敗');
      }
      setLoading(false);
    };
    if (groupId) fetchGroupAndVideos();
  }, [groupId]);

  // 申請加入群組
  const handleJoinRequest = async () => {
    if (!user || !groupId || !group) return;
    
    setSubmitting(true);
    try {
      // ✅ 等待 Firebase 初始化
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        setSnackbarMessage('Firestore 尚未初始化，請稍後再試');
        setSnackbarOpen(true);
        setSubmitting(false);
        return;
      }
      
      // 更新群組成員列表
      const groupRef = doc(db, 'groups', groupId);
      const newMember = {
        email: user.email,
        name: (user as any).displayName || user.email,
        status: '待審核',
        joinDate: new Date().toISOString(),
        message: joinMessage
      };
      
      await updateDoc(groupRef, {
        members: arrayUnion(newMember)
      });
      
      setSnackbarMessage('申請已送出！請等待管理員核准。');
      setSnackbarOpen(true);
      setShowJoinRequest(false);
      
      // 重新載入群組資料
      const groupDoc = await getDoc(groupRef);
      if (groupDoc.exists()) {
        setGroup(groupDoc.data());
      }
    } catch (error) {
      console.error('申請加入失敗:', error);
      setSnackbarMessage('申請失敗，請稍後再試。');
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  // 這裡可根據你專案的工具函式調整
  const extractYouTubeId = (url: string) => {
    if (!url) return null;
    const match = url.match(/[?&]v=([^&#]+)/) || url.match(/youtu\.be\/([^?&#]+)/);
    return match ? match[1] : null;
  };
  const formatViewCount = (count?: string) => count || '';
  const formatDate = (dateStr?: string) => dateStr || '';

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <AppBar position="static" sx={{ backgroundColor: '#154c8a' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackToHome}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <GroupIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            群組影片
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <AuthDialog open={showLogin} onClose={() => setShowLogin(false)} onShowRegister={() => {}} />
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        )}
        
        {error && (
          <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {group && !error && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <GroupIcon sx={{ mr: 1, color: '#154c8a' }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#154c8a' }}>
                {group.name}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Chip 
                label={`${videos.length} 部影片`} 
                color="primary" 
                variant="outlined"
              />
              <Chip 
                label={`${group.members?.length || 0} 位成員`} 
                color="secondary" 
                variant="outlined"
              />
            </Box>
            
            {group.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {group.description}
              </Typography>
            )}
          </Paper>
        )}
        
        {group && !error && videos.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              群組影片清單
            </Typography>
            <VideoTable
              videos={videos}
              page={0}
              rowsPerPage={100}
              total={videos.length}
              onPageChange={() => {}}
              onPlay={(video) => navigate(`/v/${video.id}`)}
              onTagChange={() => {}}
              onDelete={() => {}}
              onShare={() => {}}
              onZoneChange={() => {}}
              onOpenGroupDialog={() => {}}
              allGroups={[{ id: groupId || '', name: group.name }]}
              ZONE_OPTIONS={[]}
              extractYouTubeId={extractYouTubeId}
              formatViewCount={formatViewCount}
              formatDate={formatDate}
              columns={COLUMNS.filter(col => PAGE_COLUMN_KEYS['groupPage'].includes(col.key))}
            />
          </Paper>
        )}
        
        {group && !error && videos.length === 0 && !showJoinRequest && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              此群組目前沒有影片
            </Typography>
            <Typography variant="body2" color="text.secondary">
              群組成員可以分享影片到此群組
            </Typography>
          </Paper>
        )}

        {/* 申請加入群組 Dialog */}
        <Dialog open={showJoinRequest} onClose={() => setShowJoinRequest(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
              申請加入群組
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              您正在申請加入群組「<strong>{group?.name}</strong>」
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              請填寫申請訊息，讓群組管理員了解您加入的原因。
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="申請訊息"
              placeholder="請說明您想要加入此群組的原因..."
              value={joinMessage}
              onChange={(e) => setJoinMessage(e.target.value)}
              variant="outlined"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setShowJoinRequest(false)} variant="outlined">
              取消
            </Button>
            <Button 
              onClick={handleJoinRequest} 
              variant="contained" 
              color="primary"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
            >
              {submitting ? '送出中...' : '送出申請'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar 提示 */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Container>
    </Box>
  );
};

export default GroupPage; 